// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AcpExportStatus
 */
export const zAcpExportStatus = z.enum([
    'REQUESTED',
    'PROCESSING',
    'COMPLETED',
    'ERROR'
]);

/**
 * ChunkStatus
 *
 * Chunk status codes for sorting and extensibility (lower = higher priority)
 */
export const zChunkStatus = z.union([
    z.literal(100),
    z.literal(200),
    z.literal(300),
    z.literal(400),
    z.literal(500)
]);

/**
 * ContentKind
 *
 * The metadata kinds Reedy generates and an ONIX import can change or
 * adopt.
 */
export const zContentKind = z.enum([
    'keywords',
    'themaCodes',
    'description'
]);

/**
 * AdoptionEntry
 *
 * One kind of Reedy-generated content the publisher adopted into this
 * import -- generated content that reappears in the imported ONIX. `adopted`
 * lists all adopted items (keyword phrases or Thema codes); it is empty for
 * descriptions, which `count` alone conveys.
 */
export const zAdoptionEntry = z.object({
    kind: zContentKind,
    count: z.int(),
    adopted: z.array(z.string()).optional()
});

/**
 * FileType
 */
export const zFileType = z.enum([
    'ACP_EXPORT',
    'EVALUATION_REPORT',
    'MANUAL_ONIX_IMPORT',
    'MANUSCRIPT',
    'MANUSCRIPT_VERSION',
    'ONIX_CHUNK',
    'ONIX_EXPORT',
    'ONIX_IMPORT',
    'ONIX_IMPORT_REPORT',
    'TREND',
    'UNKNOWN'
]);

/**
 * FirestoreGenerationRequestDescriptionsData
 */
export const zFirestoreGenerationRequestDescriptionsData = z.object({
    hash: z.string().nullish(),
    specialRequest: z.string().nullish(),
    targetGroup: z.string().nullish()
});

/**
 * FirestoreGenerationStatus
 */
export const zFirestoreGenerationStatus = z.enum([
    'IMPORTED',
    'REQUESTED',
    'GENERATING',
    'GENERATED',
    'PROCESSING',
    'COMPLETED',
    'CANCELLED',
    'ERROR'
]);

/**
 * FirestoreGenerationStatusCode
 */
export const zFirestoreGenerationStatusCode = z.enum([
    '100',
    '102',
    '200',
    '402',
    '422',
    '451',
    '499',
    '500'
]);

/**
 * FirestoreOrganisationAcpSettings
 */
export const zFirestoreOrganisationAcpSettings = z.object({
    feed_id: z.string().nullish(),
    account_id: z.string().nullish(),
    target_merchant: z.string().nullish(),
    target_country: z.string().nullish()
});

/**
 * FirestoreOrganisationLanguage
 */
export const zFirestoreOrganisationLanguage = z.enum([
    'en-GB',
    'en-US',
    'de-DE'
]);

/**
 * FirestoreOrganisationLimitApiRequestsSettings
 */
export const zFirestoreOrganisationLimitApiRequestsSettings = z.object({
    interval: z.int(),
    limit: z.int()
});

/**
 * FirestoreOrganisationLimitGenerationsSettings
 */
export const zFirestoreOrganisationLimitGenerationsSettings = z.object({
    interval: z.int(),
    limit: z.int()
});

/**
 * FirestoreOrganisationMarketplace
 */
export const zFirestoreOrganisationMarketplace = z.enum([
    'UK',
    'US',
    'GER',
    'AU',
    'NONE'
]);

/**
 * FirestoreOrganisationReference
 */
export const zFirestoreOrganisationReference = z.object({
    id: z.string()
});

/**
 * FirestoreAcpExport
 */
export const zFirestoreAcpExport = z.object({
    id: z.string(),
    organisation: zFirestoreOrganisationReference,
    userId: z.string(),
    status: zAcpExportStatus.optional().default('REQUESTED'),
    count: z.int().gte(0).nullish(),
    storagePath: z.string().nullish(),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().nullish()
});

/**
 * FirestoreFile
 */
export const zFirestoreFile = z.object({
    updatedAt: z.iso.datetime(),
    createdAt: z.iso.datetime().nullable(),
    filename: z.string(),
    organisation: zFirestoreOrganisationReference,
    bucketName: z.string(),
    size: z.int().nullish(),
    mode: zFileType,
    path: z.string()
});

/**
 * FirestoreGenerationRequestDescriptions
 */
export const zFirestoreGenerationRequestDescriptions = z.object({
    id: z.string(),
    userId: z.string(),
    organisation: zFirestoreOrganisationReference,
    entityId: z.string().nullish(),
    status: zFirestoreGenerationStatus.optional().default('REQUESTED'),
    statusCode: zFirestoreGenerationStatusCode.nullish().default('100'),
    statusMessage: z.string().nullish(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullish(),
    data: zFirestoreGenerationRequestDescriptionsData
});

/**
 * FirestoreOrganisationSalesDataLake
 *
 * Which sales data lake an organisation reads from.
 */
export const zFirestoreOrganisationSalesDataLake = z.enum([
    'MMDE',
    'PMUK',
    'MMUS',
    'DISABLED'
]);

/**
 * FirestoreOrganisationTargetGroups
 */
export const zFirestoreOrganisationTargetGroups = z.object({
    name: z.string(),
    description: z.string()
});

/**
 * FirestoreProductActionsValue
 */
export const zFirestoreProductActionsValue = z.object({
    status: zFirestoreGenerationStatus.nullish(),
    statusCode: zFirestoreGenerationStatusCode.nullish(),
    msg: z.string().optional().default('')
});

/**
 * FirestoreProductActionsDescriptionsActions
 */
export const zFirestoreProductActionsDescriptionsActions = z.object({
    generate: zFirestoreProductActionsValue.optional()
});

/**
 * FirestoreProductActionsDescriptions
 */
export const zFirestoreProductActionsDescriptions = z.object({
    active: z.boolean().optional().default(false),
    errored: z.boolean().optional().default(false),
    actions: z.record(z.string(), zFirestoreProductActionsDescriptionsActions).optional()
});

/**
 * FirestoreProductActionsKeywordsActions
 */
export const zFirestoreProductActionsKeywordsActions = z.object({
    rank: zFirestoreProductActionsValue.nullish(),
    generate: zFirestoreProductActionsValue.nullish(),
    autoFill: zFirestoreProductActionsValue.nullish()
});

/**
 * FirestoreProductActionsKeywords
 */
export const zFirestoreProductActionsKeywords = z.object({
    active: z.boolean().optional().default(false),
    errored: z.boolean().optional().default(false),
    actions: zFirestoreProductActionsKeywordsActions.optional()
});

/**
 * FirestoreProductActionsManuscriptActions
 */
export const zFirestoreProductActionsManuscriptActions = z.object({
    analyse: zFirestoreProductActionsValue.nullish()
});

/**
 * FirestoreProductActionsManuscript
 */
export const zFirestoreProductActionsManuscript = z.object({
    active: z.boolean().optional().default(false),
    errored: z.boolean().optional().default(false),
    actions: zFirestoreProductActionsManuscriptActions.nullish()
});

/**
 * FirestoreProductActionsSubtitleActions
 */
export const zFirestoreProductActionsSubtitleActions = z.object({
    generate: zFirestoreProductActionsValue.nullish()
});

/**
 * FirestoreProductActionsSubtitle
 */
export const zFirestoreProductActionsSubtitle = z.object({
    active: z.boolean().optional().default(false),
    errored: z.boolean().optional().default(false),
    actions: zFirestoreProductActionsSubtitleActions.nullish()
});

/**
 * FirestoreProductActionsThemaActions
 */
export const zFirestoreProductActionsThemaActions = z.object({
    generate: zFirestoreProductActionsValue.nullish()
});

/**
 * FirestoreProductActionsThema
 */
export const zFirestoreProductActionsThema = z.object({
    active: z.boolean().optional().default(false),
    errored: z.boolean().optional().default(false),
    actions: zFirestoreProductActionsThemaActions.nullish()
});

/**
 * FirestoreProductActions
 */
export const zFirestoreProductActions = z.object({
    active: z.boolean().optional().default(false),
    errored: z.boolean().optional().default(false),
    keywords: zFirestoreProductActionsKeywords.optional(),
    descriptions: zFirestoreProductActionsDescriptions.nullish(),
    thema: zFirestoreProductActionsThema.nullish(),
    manuscript: zFirestoreProductActionsManuscript.nullish(),
    subtitle: zFirestoreProductActionsSubtitle.nullish()
});

/**
 * FirestoreProductAgeRange
 *
 * A normalized audience age range; either bound may be open-ended.
 */
export const zFirestoreProductAgeRange = z.object({
    gte: z.int().nullish(),
    lte: z.int().nullish()
});

/**
 * FirestoreProductAgeRanges
 *
 * ONIX List 30 qualifiers 16/17/18 (see ``get_age_ranges``).
 */
export const zFirestoreProductAgeRanges = z.object({
    interestAgeMonths: zFirestoreProductAgeRange.nullish(),
    interestAgeYears: zFirestoreProductAgeRange.nullish(),
    readingAgeYears: zFirestoreProductAgeRange.nullish()
});

/**
 * FirestoreProductCategorization
 */
export const zFirestoreProductCategorization = z.object({
    fiction: z.boolean().optional().default(true),
    nonFiction: z.boolean().optional().default(true),
    children: z.boolean().optional().default(true),
    updatedAt: z.iso.datetime().nullish(),
    inferredFrom: z.enum(['THEMA', 'LLM']).nullish()
});

/**
 * FirestoreProductDescription
 */
export const zFirestoreProductDescription = z.object({
    hash: z.string().optional().default(''),
    default: z.boolean().optional().default(false),
    textGenerated: z.string().optional().default(''),
    text: z.string().optional().default(''),
    textFormat: z.string().optional().default(''),
    textType: z.string().optional().default(''),
    audiences: z.array(z.string()).optional().default([])
});

/**
 * FirestoreProductDimensions
 *
 * Lengths in mm, weight in grams; keys match ``normalize_dimensions`` output.
 */
export const zFirestoreProductDimensions = z.object({
    width: z.number().nullish(),
    height: z.number().nullish(),
    thickness: z.number().nullish(),
    weight: z.number().nullish(),
    pages: z.number().nullish(),
    flaps: z.number().nullish()
});

/**
 * FirestoreProductIdentifier
 */
export const zFirestoreProductIdentifier = z.object({
    type: z.string(),
    value: z.string()
});

/**
 * FirestoreProductImages
 */
export const zFirestoreProductImages = z.object({
    frontCoverUrl: z.string().optional().default(''),
    backCoverUrl: z.string().optional().default(''),
    supportingResources: z.array(z.record(z.string(), z.unknown())).optional()
});

/**
 * FirestoreProductImportBaseProductDataCollectionSeriesIdentifier
 */
export const zFirestoreProductImportBaseProductDataCollectionSeriesIdentifier = z.object({
    collectionIDType: z.string().nullish(),
    idTypeName: z.string().nullish(),
    idValue: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataCollectionTitle
 */
export const zFirestoreProductImportBaseProductDataCollectionTitle = z.object({
    titleType: z.string().nullish(),
    titleText: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataContributorItem
 */
export const zFirestoreProductImportBaseProductDataContributorItem = z.object({
    contributorRoles: z.array(z.string()).nullish().default([]),
    sequenceNumber: z.string().nullish(),
    personName: z.string().nullish(),
    personNameInverted: z.string().nullish(),
    biographicalNote: z.string().nullish(),
    personNameKey: z.string().nullish(),
    personNameBeforeKey: z.string().nullish(),
    corporateName: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataCollections
 */
export const zFirestoreProductImportBaseProductDataCollections = z.object({
    mainTitle: z.string().nullish(),
    numberWithinSeries: z.string().nullish(),
    seriesIdentifiers: z.array(zFirestoreProductImportBaseProductDataCollectionSeriesIdentifier).nullish(),
    titles: z.array(zFirestoreProductImportBaseProductDataCollectionTitle).nullish(),
    contributors: z.array(zFirestoreProductImportBaseProductDataContributorItem).nullish()
});

/**
 * FirestoreProductImportBaseProductDataDescriptionAudienceRange
 */
export const zFirestoreProductImportBaseProductDataDescriptionAudienceRange = z.object({
    audienceRangeQualifier: z.string(),
    audienceRangeValues: z.array(z.string()),
    audienceRangePrecisions: z.array(z.string())
});

/**
 * FirestoreProductImportBaseProductDataDescriptionAudiencesItem
 */
export const zFirestoreProductImportBaseProductDataDescriptionAudiencesItem = z.object({
    audienceCodeType: z.string(),
    audienceCodeValue: z.string(),
    audienceCodeTypeName: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataDescriptionLanguage
 */
export const zFirestoreProductImportBaseProductDataDescriptionLanguage = z.object({
    languageRole: z.string(),
    languageCode: z.string()
});

/**
 * FirestoreProductImportBaseProductDataDescription
 */
export const zFirestoreProductImportBaseProductDataDescription = z.object({
    productForm: z.string().nullish(),
    productFormDetail: z.string().nullish(),
    numberOfPages: z.string().nullish(),
    editionType: z.string().nullish(),
    editionNumber: z.int().nullish(),
    audienceDescription: z.string().nullish(),
    languages: z.array(zFirestoreProductImportBaseProductDataDescriptionLanguage),
    audiences: z.array(zFirestoreProductImportBaseProductDataDescriptionAudiencesItem),
    audienceRange: zFirestoreProductImportBaseProductDataDescriptionAudienceRange.nullish()
});

/**
 * FirestoreProductImportBaseProductDataImprints
 */
export const zFirestoreProductImportBaseProductDataImprints = z.object({
    imprintName: z.string()
});

/**
 * FirestoreProductImportBaseProductDataInfoProductIdItem
 */
export const zFirestoreProductImportBaseProductDataInfoProductIdItem = z.object({
    productIDType: z.string(),
    idValue: z.string(),
    idTypeName: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataInfo
 */
export const zFirestoreProductImportBaseProductDataInfo = z.object({
    productIds: z.array(zFirestoreProductImportBaseProductDataInfoProductIdItem),
    recordReference: z.string(),
    notificationType: z.string()
});

/**
 * FirestoreProductImportBaseProductDataPublisherItem
 */
export const zFirestoreProductImportBaseProductDataPublisherItem = z.object({
    publisherName: z.string().nullish(),
    publishingRole: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataPublishingDetails
 */
export const zFirestoreProductImportBaseProductDataPublishingDetails = z.object({
    publicationDate: z.string().nullish(),
    outOfPrintDate: z.string().nullish(),
    countryOfPublication: z.string().nullish(),
    cityOfPublication: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataSalesRights
 */
export const zFirestoreProductImportBaseProductDataSalesRights = z.object({
    salesRightsType: z.string(),
    countries: z.array(z.string()).nullish().default([]),
    regions: z.array(z.string()).nullish().default([]),
    rightRegions: z.array(z.string()).nullish(),
    countriesExcluded: z.array(z.string()).nullish(),
    regionsExcluded: z.array(z.string()).nullish()
});

/**
 * FirestoreProductImportBaseProductDataSubjectsItem
 */
export const zFirestoreProductImportBaseProductDataSubjectsItem = z.object({
    subjectSchemeIdentifier: z.string().nullish(),
    subjectHeadingText: z.string().nullish(),
    subjectCode: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataSubjects
 */
export const zFirestoreProductImportBaseProductDataSubjects = z.object({
    Thema_subject_category: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Keywords: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BISAC_Subject_Heading: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_subject_category: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_time_period_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Thema_time_period_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Thema_place_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Dewey: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Abridged_Dewey: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    LC_classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    LC_subject_heading: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NLM_classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    MeSH_heading: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NAL_subject_heading: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    AAT: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    UDC: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BISAC_Regional_theme: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_geographical_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_language_qualifier_language_as_subject: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_educational_purpose_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_reading_level_and_special_interest_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    DDC_Sachgruppen_der_Deutschen_Nationalbibliografie: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    LC_fiction_genre_heading: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_childrens_book_marketing_category: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BISAC_Merchandising_Theme: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Publishers_own_category_code: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Proprietary_subject_scheme: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Tabla_de_materias_ISBN: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Warengruppen_Systematik_des_deutschen_Buchhandels: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    SWD: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Themes_Electre: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    CLIL_Classification_thematique: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    DNB_Sachgruppen: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NUGI: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NUR: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    ECPA_Christian_Book_Category: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    SISO: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Korean_Decimal_Classification_KDC: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    DDC_Deutsch_22: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Bokgrupper: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Varegrupper: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Lreplaner_KL06: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Nippon_Decimal_Classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BSQ: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    ANELE_Materias: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Utdanningsprogram: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Programomrade: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Undervisningsmateriell: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Norsk_DDK: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Varugrupper: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    SAB: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Laromedelstyp: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Forhandsbeskrivning: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Spanish_ISBN_UDC_subset: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    ECI_subject_categories: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Soggetto_CCE: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Qualificatore_geografico_CCE: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Qualificatore_di_lingua_CCE: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Qualificatore_di_periodo_storico_CCE: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Qualificatore_di_livello_scolastico_CCE: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Qualificatore_di_eta_di_lettura_CCE: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    VdS_Bildungsmedien_Facher: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Fagkoder: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    JEL_classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    CSH: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    RVM: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    YSA: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Allars: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    YKL: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    MUSA: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    CILLA: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Kaunokki: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Bella: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    YSO: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    PTO: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Suomalainen_kirja_alan_luokitus: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Sears: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_E4L: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    CSR: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Suomalainen_oppiaineluokitus: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Japanese_book_trade_C_Code: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Japanese_book_trade_Genre_Code: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Fiktiivisen_aineiston_lisaluokitus: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Arabic_Subject_heading_scheme: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Arabized_BIC_subject_category: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Arabized_LC_subject_headings: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Bibliotheca_Alexandrina_Subject_Headings: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Postal_code: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    GeoNames_ID: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NewBooks_Subject_Classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Chinese_Library_Classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NTCPDSAC_Classification: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Season_and_Event_Indicator: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    GND: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BIC_UKSLC: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Thema_educational_purpose_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Thema_interest_age_special_interest_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Thema_style_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Thema_language_qualifier: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Amnesord: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Statystyka_Ksiazek_Papierowych_Mowionych_I_Elektronicznych: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    CCSS: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Rameau: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Nomenclature_discipline_scolaire: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    ISIC: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    LC_Childrens_Subject_Headings: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Ny_Laromedel: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    EuroVoc: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    BISG_Educational_Taxonomy: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Keywords_not_for_display: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Nomenclature_Diplome: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Key_character_names: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Key_place_names: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    FAST: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NGSS: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    MVB_Lesemotive: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    LOPS21_Subject_module: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Lreplaner_LK20: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Kompetansemal_LK20: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Kompetansemalsett_LK20: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Tverrfaglige_temaer_LK20: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    CLIL_Type_darticle_scolaire: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    GAR_Type_pedagogique: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    ISCED_F: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    Klassifikationen_von_Spielen_Puzzles_und_Spielwaren: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    NBVok_NTSF: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    JPRO_Genre: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    KAUNO: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    SLM: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish(),
    YSO_places: z.array(zFirestoreProductImportBaseProductDataSubjectsItem).nullish()
});

/**
 * FirestoreProductImportBaseProductDataSupplyDetailsItemPrice
 */
export const zFirestoreProductImportBaseProductDataSupplyDetailsItemPrice = z.object({
    priceType: z.string().nullish(),
    priceAmountAsStr: z.string().nullish(),
    priceAmount: z.number().nullable(),
    currencyCode: z.string().nullish()
});

/**
 * FirestoreProductImportBaseProductDataSupplyDetailsItem
 */
export const zFirestoreProductImportBaseProductDataSupplyDetailsItem = z.object({
    supplierName: z.string().nullish(),
    supplierRole: z.string().nullish(),
    availability: z.string().nullish(),
    prices: z.array(zFirestoreProductImportBaseProductDataSupplyDetailsItemPrice).nullish()
});

/**
 * FirestoreProductImportBaseProductDataText
 */
export const zFirestoreProductImportBaseProductDataText = z.object({
    default: z.boolean().nullish().default(false),
    hash: z.string().nullish(),
    textType: z.string().nullish(),
    text: z.string().nullish(),
    textFormat: z.string().nullish(),
    audiences: z.array(z.string()).nullish().default([])
});

/**
 * FirestoreProductImportBaseProductDataTitleItem
 */
export const zFirestoreProductImportBaseProductDataTitleItem = z.object({
    titleType: z.string().nullish(),
    subtitle: z.string().nullish(),
    titleText: z.string().nullish(),
    prefix: z.string().nullish().default(''),
    titleWithoutPrefix: z.string().nullish().default('')
});

/**
 * FirestoreProductImportBaseProductData
 */
export const zFirestoreProductImportBaseProductData = z.object({
    titles: z.array(zFirestoreProductImportBaseProductDataTitleItem).min(1),
    texts: z.array(zFirestoreProductImportBaseProductDataText),
    contributors: z.array(zFirestoreProductImportBaseProductDataContributorItem),
    subjects: zFirestoreProductImportBaseProductDataSubjects,
    publishers: z.array(zFirestoreProductImportBaseProductDataPublisherItem).nullish().default([]),
    salesRightss: z.array(zFirestoreProductImportBaseProductDataSalesRights).nullable(),
    imprints: z.array(zFirestoreProductImportBaseProductDataImprints),
    collections: z.array(zFirestoreProductImportBaseProductDataCollections),
    description: zFirestoreProductImportBaseProductDataDescription,
    supplyDetails: z.array(zFirestoreProductImportBaseProductDataSupplyDetailsItem).nullish().default([]),
    info: zFirestoreProductImportBaseProductDataInfo,
    publishingDetails: zFirestoreProductImportBaseProductDataPublishingDetails
});

/**
 * FirestoreProductImportData
 */
export const zFirestoreProductImportData = z.object({
    importId: z.string(),
    productsImportId: z.string(),
    objectName: z.string(),
    folderName: z.string(),
    fileName: z.string(),
    chunkId: z.string().nullish()
});

/**
 * FirestoreProductImportHeaderData
 */
export const zFirestoreProductImportHeaderData = z.object({
    senderName: z.string(),
    sentDateTime: z.string(),
    senderEmail: z.string().nullish(),
    senderContactName: z.string().nullish(),
    addressees: z.array(z.string()).nullish()
});

/**
 * FirestoreProductImportImportData
 */
export const zFirestoreProductImportImportData = z.object({
    bucketName: z.string(),
    importId: z.string(),
    chunkId: z.string().nullish(),
    objectName: z.string(),
    folderName: z.string().nullish(),
    fileName: z.string().nullish(),
    projectId: z.string(),
    onixRelease: z.string(),
    onixVersion: z.string(),
    productCount: z.int().nullish(),
    productGlobalCount: z.int().nullish(),
    sourceName: z.string().nullish(),
    reportingTopicName: z.string().nullish(),
    topicName: z.string().nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailResourceLink
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetailResourceLink = z.object({
    value: z.string()
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailResourceVersion
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetailResourceVersion = z.object({
    resourceLinks: z.array(zFirestoreProductImportProductOnixV3DataCollateralDetailResourceLink).nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailTextContentTextTextFormatValue
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetailTextContentTextTextFormatValue = z.object({
    value: z.string()
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailTextContentText
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetailTextContentText = z.object({
    textformat: z.union([
        z.string(),
        zFirestoreProductImportProductOnixV3DataCollateralDetailTextContentTextTextFormatValue
    ]).nullish(),
    value: z.string()
});

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsiteLink
 */
export const zFirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsiteLink = z.object({
    value: z.string()
});

/**
 * FirestoreProductImportProductOnixV3DataValueContainer
 */
export const zFirestoreProductImportProductOnixV3DataValueContainer = z.object({
    value: z.string()
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailSupportingResource
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetailSupportingResource = z.object({
    resourceContentType: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    resourceMode: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    resourceVersions: z.array(zFirestoreProductImportProductOnixV3DataCollateralDetailResourceVersion).nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailTextContent
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetailTextContent = z.object({
    contentAudiences: z.array(zFirestoreProductImportProductOnixV3DataValueContainer),
    textType: zFirestoreProductImportProductOnixV3DataValueContainer,
    texts: z.array(zFirestoreProductImportProductOnixV3DataCollateralDetailTextContentText)
});

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetail
 */
export const zFirestoreProductImportProductOnixV3DataCollateralDetail = z.object({
    textContents: z.array(zFirestoreProductImportProductOnixV3DataCollateralDetailTextContent).nullish(),
    supportingResources: z.array(zFirestoreProductImportProductOnixV3DataCollateralDetailSupportingResource).nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataDescriptiveDetail
 */
export const zFirestoreProductImportProductOnixV3DataDescriptiveDetail = z.object({
    productFormDetails: z.array(zFirestoreProductImportProductOnixV3DataValueContainer).nullish().default([]),
    productForm: zFirestoreProductImportProductOnixV3DataValueContainer.nullable()
});

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsite
 */
export const zFirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsite = z.object({
    websiteRole: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    websiteLinks: z.array(zFirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsiteLink).nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetailPublisher
 */
export const zFirestoreProductImportProductOnixV3DataPublishingDetailPublisher = z.object({
    publisherNames: z.array(zFirestoreProductImportProductOnixV3DataValueContainer).nullish(),
    publishingRole: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    websites: z.array(zFirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsite).nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetail
 */
export const zFirestoreProductImportProductOnixV3DataPublishingDetail = z.object({
    publishers: z.array(zFirestoreProductImportProductOnixV3DataPublishingDetailPublisher).nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProductIdentifiers
 */
export const zFirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProductIdentifiers = z.object({
    productIDType: zFirestoreProductImportProductOnixV3DataValueContainer,
    idValue: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    idTypeName: zFirestoreProductImportProductOnixV3DataValueContainer.nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProduct
 */
export const zFirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProduct = z.object({
    productIdentifiers: z.array(zFirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProductIdentifiers),
    productForm: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    productRelationCodes: z.array(zFirestoreProductImportProductOnixV3DataValueContainer).nullish().default([]),
    productFormDetails: z.array(zFirestoreProductImportProductOnixV3DataValueContainer).nullish().default([])
});

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialWorksIdentifier
 */
export const zFirestoreProductImportProductOnixV3DataRelatedMaterialWorksIdentifier = z.object({
    idValue: zFirestoreProductImportProductOnixV3DataValueContainer,
    workIDType: zFirestoreProductImportProductOnixV3DataValueContainer.nullish(),
    idTypeName: zFirestoreProductImportProductOnixV3DataValueContainer.nullish()
});

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialWorks
 */
export const zFirestoreProductImportProductOnixV3DataRelatedMaterialWorks = z.object({
    workRelationCode: zFirestoreProductImportProductOnixV3DataValueContainer,
    workIdentifiers: z.array(zFirestoreProductImportProductOnixV3DataRelatedMaterialWorksIdentifier)
});

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterial
 */
export const zFirestoreProductImportProductOnixV3DataRelatedMaterial = z.object({
    relatedProducts: z.array(zFirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProduct).nullish().default([]),
    relatedWorks: z.array(zFirestoreProductImportProductOnixV3DataRelatedMaterialWorks).nullish().default([])
});

/**
 * FirestoreProductImportProductOnixV3Data
 */
export const zFirestoreProductImportProductOnixV3Data = z.object({
    descriptiveDetail: zFirestoreProductImportProductOnixV3DataDescriptiveDetail,
    relatedMaterial: zFirestoreProductImportProductOnixV3DataRelatedMaterial.nullish(),
    collateralDetail: zFirestoreProductImportProductOnixV3DataCollateralDetail.nullish(),
    publishingDetail: zFirestoreProductImportProductOnixV3DataPublishingDetail.nullish()
});

/**
 * FirestoreProductImport
 */
export const zFirestoreProductImport = z.object({
    id: z.string().nullable(),
    baseProduct: zFirestoreProductImportBaseProductData,
    product: zFirestoreProductImportProductOnixV3Data,
    importData: zFirestoreProductImportImportData,
    header: zFirestoreProductImportHeaderData
});

/**
 * FirestoreProductManuscriptAnalysis
 */
export const zFirestoreProductManuscriptAnalysis = z.object({
    result: z.string().nullish(),
    generatedContentId: z.string().nullish(),
    generatedAt: z.iso.datetime().nullish(),
    generationRequestId: z.string().nullish()
});

/**
 * FirestoreProductManuscript
 */
export const zFirestoreProductManuscript = z.object({
    filename: z.string(),
    uploadedAt: z.iso.datetime(),
    analysis: zFirestoreProductManuscriptAnalysis.nullish()
});

/**
 * FirestoreProductPublisher
 */
export const zFirestoreProductPublisher = z.object({
    name: z.string(),
    role: z.string().nullable()
});

/**
 * FirestoreProductQuote
 */
export const zFirestoreProductQuote = z.object({
    text: z.string().optional().default(''),
    source: z.string().optional().default('')
});

/**
 * FirestoreProductSeriesIdentifier
 */
export const zFirestoreProductSeriesIdentifier = z.object({
    type: z.string(),
    idTypeName: z.string().nullish(),
    value: z.string()
});

/**
 * FirestoreProductSubtitleCandidate
 */
export const zFirestoreProductSubtitleCandidate = z.object({
    subtitle: z.string(),
    reasoning: z.string(),
    isBestChoice: z.boolean().nullish(),
    bestChoiceReasoning: z.string().nullish()
});

/**
 * FirestoreUserApiKey
 */
export const zFirestoreUserApiKey = z.object({
    identifier: z.string(),
    name: z.string().nullish(),
    apiKeyShort: z.string(),
    createdAt: z.iso.datetime()
});

/**
 * FirestoreWeeklySearchTerm
 */
export const zFirestoreWeeklySearchTerm = z.object({
    id: z.string(),
    term: z.string(),
    current_rank: z.int(),
    movements_indicator: z.enum([
        'up',
        'down',
        'no_change',
        'new'
    ]),
    movement_delta: z.int().nullish(),
    frequency_rank: z.int()
});

/**
 * FirestoreWeeklySearchTermsDoc
 */
export const zFirestoreWeeklySearchTermsDoc = z.object({
    id: z.string(),
    market: z.string(),
    week: z.string(),
    previous_week: z.string().nullish(),
    updated_at: z.iso.datetime(),
    import_date: z.iso.datetime(),
    terms: z.array(zFirestoreWeeklySearchTerm)
});

/**
 * GenerationEventData
 *
 * A single generation. Keyword, Thema and description generation run as
 * separate jobs, so each is its own event -- one `kind` per document. `count`
 * is how many were produced, for the popover; it is None for descriptions,
 * which always produce one.
 */
export const zGenerationEventData = z.object({
    kind: zContentKind,
    count: z.int().nullish()
});

export const zJsonValue = z.unknown();

/**
 * ManuscriptProcessingError
 */
export const zManuscriptProcessingError = z.object({
    code: z.string(),
    message: z.string()
});

/**
 * ManuscriptProcessingStatus
 */
export const zManuscriptProcessingStatus = z.enum([
    'matched',
    'queued',
    'unmatched',
    'uploading',
    'processing',
    'complete',
    'error',
    'skipped_duplicate'
]);

/**
 * FirestoreManuscriptProcessingReporting
 *
 * One entry per received manuscript version, driving the admin panel.
 */
export const zFirestoreManuscriptProcessingReporting = z.object({
    id: z.string(),
    organisation: zFirestoreOrganisationReference,
    status: zManuscriptProcessingStatus,
    fileName: z.string(),
    isbn: z.string(),
    contentHash: z.string(),
    objectPath: z.string(),
    contentClaimed: z.boolean().optional().default(false),
    duplicateOfReportId: z.string().nullish(),
    productId: z.string().nullish(),
    productTitle: z.string().nullish(),
    workId: z.string().nullish(),
    jobId: z.string().nullish(),
    submissionTaskId: z.string().nullish(),
    semanticDataId: z.string().nullish(),
    error: zManuscriptProcessingError.nullish(),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().nullish()
});

/**
 * MetadataEventMeta
 *
 * Links the event back to the source record it was emitted from.
 */
export const zMetadataEventMeta = z.object({
    importId: z.string().nullish(),
    generationRequestId: z.string().nullish()
});

/**
 * GenerationMetadataEvent
 */
export const zGenerationMetadataEvent = z.object({
    id: z.string(),
    productId: z.string(),
    organisation: zFirestoreOrganisationReference,
    occurredAt: z.iso.datetime(),
    meta: zMetadataEventMeta.optional(),
    type: z.literal('GENERATION').optional().default('GENERATION'),
    generation: zGenerationEventData
});

/**
 * OnixChunkReportingOperation
 */
export const zOnixChunkReportingOperation = z.enum(['ONIX_PARSING', 'PRODUCT_INSERT']);

/**
 * OnixChunkReporting
 */
export const zOnixChunkReporting = z.object({
    id: z.string(),
    fileName: z.string(),
    errors: z.array(z.string()).optional().default([]),
    createdAt: z.string(),
    success: z.array(zOnixChunkReportingOperation).optional().default([]),
    failed: z.array(zOnixChunkReportingOperation).optional().default([]),
    reportId: z.string().nullish().default(''),
    isbn: z.string().nullish().default(''),
    errorMessage: z.string().nullish().default(''),
    timestamp: z.string().nullish().default(''),
    productId: z.string().nullish().default(''),
    status: zChunkStatus.nullish()
});

/**
 * OnixExportStatus
 */
export const zOnixExportStatus = z.enum([
    'REQUESTED',
    'PROCESSING',
    'COMPLETED',
    'ERROR'
]);

/**
 * FirestoreOnixExport
 */
export const zFirestoreOnixExport = z.object({
    id: z.string(),
    organisation: zFirestoreOrganisationReference,
    productIds: z.array(z.string()).optional().default([]),
    status: zOnixExportStatus.optional().default('REQUESTED'),
    userId: z.string(),
    createdAt: z.iso.datetime().nullish(),
    updatedAt: z.iso.datetime().nullish(),
    fileName: z.string().nullish()
});

/**
 * OnixImportCounts
 *
 * Counts shown in the import marker's popover: subject schemes (keywords,
 * Thema categories) plus how many descriptions the ONIX record carried.
 */
export const zOnixImportCounts = z.object({
    keywords: z.int().optional().default(0),
    themaCodes: z.int().optional().default(0),
    descriptions: z.int().optional().default(0)
});

/**
 * OnixImportEventData
 */
export const zOnixImportEventData = z.object({
    sourceFileName: z.string().nullish(),
    changed: z.array(zContentKind).optional(),
    counts: zOnixImportCounts.optional(),
    adoption: z.array(zAdoptionEntry).optional()
});

/**
 * OnixImportMetadataEvent
 */
export const zOnixImportMetadataEvent = z.object({
    id: z.string(),
    productId: z.string(),
    organisation: zFirestoreOrganisationReference,
    occurredAt: z.iso.datetime(),
    meta: zMetadataEventMeta.optional(),
    type: z.literal('ONIX_IMPORT').optional().default('ONIX_IMPORT'),
    onixImport: zOnixImportEventData
});

/**
 * OpenAIStatus
 */
export const zOpenAiStatus = z.enum([
    'VALIDATING',
    'FAILED',
    'IN_PROGRESS',
    'COMPLETED',
    'EXPIRED',
    'CANCELLED',
    'CANCELLING'
]);

/**
 * FirestoreGenerationRequestEvaluationData
 */
export const zFirestoreGenerationRequestEvaluationData = z.object({
    mode: z.enum(['SYNC', 'ASYNC']).optional().default('ASYNC'),
    requestedProducts: z.union([
        z.literal('ALL'),
        z.array(z.string())
    ]),
    async_batch_id: z.string().nullish(),
    async_batch_status: zOpenAiStatus.nullish(),
    file: z.record(z.string(), z.unknown()).nullish(),
    last_evaluated_identifier: z.string().nullish()
});

/**
 * FirestoreGenerationRequestEvaluation
 */
export const zFirestoreGenerationRequestEvaluation = z.object({
    id: z.string(),
    userId: z.string(),
    organisation: zFirestoreOrganisationReference,
    entityId: z.string().nullish(),
    status: zFirestoreGenerationStatus.optional().default('REQUESTED'),
    statusCode: zFirestoreGenerationStatusCode.nullish().default('100'),
    statusMessage: z.string().nullish(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullish(),
    data: zFirestoreGenerationRequestEvaluationData
});

export const zProductAvailabilityCodeLiteral = z.enum([
    '01',
    '09',
    '10',
    '11',
    '12',
    '20',
    '21',
    '22',
    '23',
    '30',
    '31',
    '32',
    '33',
    '34',
    '40',
    '41',
    '42',
    '43',
    '44',
    '45',
    '46',
    '47',
    '48',
    '49',
    '50',
    '51',
    '52',
    '97',
    '98',
    '99'
]);

/**
 * FirestoreOrganisationSettings
 */
export const zFirestoreOrganisationSettings = z.object({
    inactiveProductStatus: z.array(zProductAvailabilityCodeLiteral).optional(),
    excludeNoAvailabilityCode: z.boolean().optional().default(false),
    canSeeAmazonSearchTerms: z.boolean().nullish().default(false),
    marketplace: zFirestoreOrganisationMarketplace.nullish().default('NONE'),
    language: zFirestoreOrganisationLanguage.nullish().default('en-US'),
    acpSettings: zFirestoreOrganisationAcpSettings.nullish(),
    salesDataLake: zFirestoreOrganisationSalesDataLake.optional().default('DISABLED')
});

/**
 * FirestoreOrganisation
 */
export const zFirestoreOrganisation = z.object({
    id: z.string(),
    name: z.string(),
    parent_organisation: z.string().nullish(),
    emails: z.array(z.string()).nullish().default([]),
    styles: z.union([
        z.array(z.string()),
        z.record(z.string(), z.array(z.string()))
    ]).nullish().default([]),
    targets: z.array(z.string()).nullish().default([]),
    targetGroups: z.array(zFirestoreOrganisationTargetGroups).nullish().default([]),
    limitApiRequests: z.array(zFirestoreOrganisationLimitApiRequestsSettings).nullish().default([]),
    limitGenerations: z.array(zFirestoreOrganisationLimitGenerationsSettings).nullish().default([]),
    disablePromptGuard: z.boolean().nullish().default(false),
    settings: zFirestoreOrganisationSettings.optional(),
    descriptionsDefault: z.string().nullish().default('Description'),
    descriptionsTypes: z.record(z.string(), z.int()).nullish().default({}),
    createdAt: z.iso.datetime().optional(),
    deletedAt: z.iso.datetime().nullish(),
    hardDeletedAt: z.iso.datetime().nullish()
});

export const zProductAvailabilityNameLiteral = z.enum([
    'Cancelled',
    'Not_yet_available_postponed_indefinitely',
    'Not_yet_available',
    'Awaiting_stock',
    'Not_yet_available_will_be_POD',
    'Available',
    'In_stock',
    'To_order',
    'POD',
    'Temporarily_unavailable',
    'Out_of_stock',
    'Reprinting',
    'Awaiting_reissue',
    'Temporarily_withdrawn_from_sale',
    'Not_available_reason_unspecified',
    'Not_available_replaced_by_new_product',
    'Not_available_other_format_available',
    'No_longer_supplied_by_the_supplier',
    'Apply_direct',
    'Not_sold_separately',
    'Withdrawn_from_sale',
    'Remaindered',
    'Not_available_replaced_by_POD',
    'Recalled',
    'Not_sold_as_set',
    'Not_available_publisher_indicates_OP',
    'Not_available_publisher_no_longer_sells_product_in_this_market',
    'No_recent_update_received',
    'No_longer_receiving_updates',
    'Contact_supplier'
]);

/**
 * FirestoreProductSupplyDetail
 */
export const zFirestoreProductSupplyDetail = z.object({
    supplierName: z.string().nullish(),
    supplierRole: z.string().nullish(),
    availability: zProductAvailabilityNameLiteral.optional().default('Contact_supplier')
});

export const zProductFormCodeLiteral = z.enum([
    '00',
    'AA',
    'AB',
    'AC',
    'AD',
    'AE',
    'AF',
    'AG',
    'AH',
    'AI',
    'AJ',
    'AK',
    'AL',
    'AM',
    'AN',
    'AO',
    'AZ',
    'BA',
    'BB',
    'BC',
    'BD',
    'BE',
    'BF',
    'BG',
    'BH',
    'BI',
    'BJ',
    'BK',
    'BL',
    'BM',
    'BN',
    'BO',
    'BP',
    'BZ',
    'CA',
    'CB',
    'CC',
    'CD',
    'CE',
    'CZ',
    'DA',
    'DB',
    'DC',
    'DE',
    'DF',
    'DI',
    'DJ',
    'DK',
    'DL',
    'DM',
    'DN',
    'DO',
    'DZ',
    'EA',
    'EB',
    'EC',
    'ED',
    'FA',
    'FC',
    'FD',
    'FE',
    'FF',
    'FZ',
    'LA',
    'LB',
    'LC',
    'MA',
    'MB',
    'MC',
    'MZ',
    'PA',
    'PB',
    'PC',
    'PD',
    'PE',
    'PF',
    'PG',
    'PH',
    'PI',
    'PJ',
    'PK',
    'PL',
    'PM',
    'PN',
    'PO',
    'PP',
    'PQ',
    'PR',
    'PS',
    'PT',
    'PU',
    'PV',
    'PZ',
    'SA',
    'SB',
    'SC',
    'SD',
    'SE',
    'SF',
    'SG',
    'VA',
    'VF',
    'VI',
    'VJ',
    'VK',
    'VL',
    'VM',
    'VN',
    'VO',
    'VP',
    'VQ',
    'VZ',
    'XA',
    'XB',
    'XC',
    'XD',
    'XE',
    'XF',
    'XG',
    'XH',
    'XI',
    'XJ',
    'XK',
    'XL',
    'XM',
    'XN',
    'XO',
    'XY',
    'XZ',
    'ZA',
    'ZB',
    'ZC',
    'ZD',
    'ZE',
    'ZF',
    'ZG',
    'ZH',
    'ZI',
    'ZJ',
    'ZK',
    'ZL',
    'ZM',
    'ZN',
    'ZO',
    'ZP',
    'ZX',
    'ZY',
    'ZZ'
]);

export const zProductFormDescLiteral = z.enum([
    'Undefined',
    'Audio',
    'Audio cassette',
    'CD-Audio',
    'DAT',
    'Audio disc',
    'Audio tape',
    'MiniDisc',
    'CD-Extra',
    'DVD Audio',
    'Downloadable audio file',
    'Pre-recorded digital audio player',
    'Pre-recorded SD card',
    'LP',
    'Downloadable and online audio file',
    'Online audio file',
    'Other audio format',
    'Book',
    'Hardback',
    'Paperback / softback',
    'Loose-leaf',
    'Spiral bound',
    'Pamphlet',
    'Leather / fine binding',
    'Board book',
    'Rag book',
    'Bath book',
    'Novelty book',
    'Slide bound',
    'Big book',
    'Part-work (fascículo)',
    'Fold-out book or chart',
    'Foam book',
    'Other book format',
    'Sheet map',
    'Sheet map, folded',
    'Sheet map, flat',
    'Sheet map, rolled',
    'Globe',
    'Other cartographic',
    'Digital (on physical carrier)',
    'CD-ROM',
    'CD-I',
    'Game cartridge',
    'Diskette',
    'DVD-ROM',
    'Secure Digital (SD) Memory Card',
    'Compact Flash Memory Card',
    'Memory Stick Memory Card',
    'USB Flash Drive',
    'Double-sided CD/DVD',
    'BR-ROM',
    'Other digital carrier',
    'Digital (delivered electronically)',
    'Digital download and online',
    'Digital online',
    'Digital download',
    'Film or transparency',
    'Slides',
    'OHP transparencies',
    'Filmstrip',
    'Film',
    'Other film or transparency format',
    'Digital product license',
    'Digital product license key',
    'Digital product license code',
    'Microform',
    'Microfiche',
    'Microfilm',
    'Other microform',
    'Miscellaneous print',
    'Address book',
    'Calendar',
    'Cards',
    'Copymasters',
    'Diary or journal',
    'Frieze',
    'Kit',
    'Sheet music',
    'Postcard book or pack',
    'Poster',
    'Record book',
    'Wallet or folder',
    'Pictures or photographs',
    'Wallchart',
    'Stickers',
    'Plate (lámina)',
    'Notebook / blank book',
    'Organizer',
    'Bookmark',
    'Leaflet',
    'Book plates',
    'Other printed item',
    'Multiple-component retail product',
    'Multiple-component retail product, boxed',
    'Multiple-component retail product, slip-cased',
    'Multiple-component retail product, shrink-wrapped',
    'Multiple-component retail product, loose',
    'Multiple-component retail product, part(s) enclosed',
    'Multiple-component retail product, entirely digital',
    'Video',
    'Videodisc',
    'DVD video',
    'VHS video',
    'Betamax video',
    'VCD',
    'SVCD',
    'HD DVD',
    'Blu-ray',
    'UMD Video',
    'CBHD',
    'Other video format',
    'Trade-only material',
    'Dumpbin – empty',
    'Dumpbin – filled',
    'Counterpack – empty',
    'Counterpack – filled',
    'Poster, promotional',
    'Shelf strip',
    'Window piece',
    'Streamer',
    'Spinner – empty',
    'Large book display',
    'Shrink-wrapped pack',
    'Boxed pack',
    'Pack (outer packaging unspecified)',
    'Spinner – filled',
    'Other point of sale – including retail product',
    'Other point of sale',
    'General merchandise',
    'Doll or figure',
    'Soft toy',
    'Toy',
    'Game',
    'T-shirt',
    'E-book reader',
    'Tablet computer',
    'Audiobook player',
    'Jigsaw',
    'Mug',
    'Tote bag',
    'Tableware',
    'Umbrella',
    'Paints, crayons, pencils',
    'Handicraft kit',
    'Other toy/game accessories',
    'Other apparel',
    'Other merchandise'
]);

/**
 * PROJECT_TYPE
 */
export const zProjectType = z.enum([
    'isbns',
    'google-trends',
    'amazon-trends'
]);

/**
 * FirestoreProject
 */
export const zFirestoreProject = z.object({
    type: zProjectType,
    name: z.string(),
    isbns: z.array(z.string()),
    createdAt: z.iso.datetime(),
    userId: z.string(),
    organisation: zFirestoreOrganisationReference,
    searchOptions: z.record(z.string(), z.string()).optional().default({}),
    amplifyProjectId: z.string().nullish()
});

/**
 * PipelineImportReportError
 */
export const zPipelineImportReportError = z.object({
    level: z.string().nullish(),
    validator: z.string().nullish(),
    location: z.string().nullish(),
    message: z.string().nullish(),
    error_type: z.string().nullish()
});

/**
 * PipelineImportReport
 */
export const zPipelineImportReport = z.object({
    importId: z.string(),
    bucketName: z.string(),
    objectName: z.string(),
    fileName: z.string(),
    createdAt: z.string(),
    folderName: z.string(),
    error: zPipelineImportReportError.nullish(),
    errors: z.array(zPipelineImportReportError).optional().default([]),
    projectId: z.string(),
    completedAt: z.string().nullish(),
    globalIsbns: z.array(z.string()).optional().default([]),
    importedIsbns: z.array(z.string()).optional().default([]),
    filteredIsbns: z.array(z.string()).optional().default([]),
    failedIsbns: z.array(z.string()).optional().default([]),
    productsGlobalCount: z.int(),
    productsFilteredCount: z.int(),
    productsImportedCount: z.int(),
    productsFailedCount: z.int(),
    success: z.boolean(),
    description: z.string().optional().default(''),
    organisation: zFirestoreOrganisationReference
});

/**
 * PipelineImportReportStatus
 */
export const zPipelineImportReportStatus = z.enum([
    'processing',
    'completed',
    'failed',
    'timeout'
]);

/**
 * OnixImportPipelineReporting
 */
export const zOnixImportPipelineReporting = z.object({
    id: z.string(),
    bucketName: z.string(),
    objectName: z.string(),
    fileName: z.string(),
    createdAt: z.string(),
    folderName: z.string(),
    organisation: zFirestoreOrganisationReference,
    mode: z.string().nullish(),
    status: zPipelineImportReportStatus.optional().default('processing'),
    totalFiles: z.int().optional().default(0),
    filesCreatedFromSplitCount: z.int().nullish(),
    filesUploadedCount: z.int().nullish(),
    tasksScheduledSuccessfully: z.int().optional().default(0),
    tasksScheduledFailed: z.int().optional().default(0),
    hasErrors: z.boolean().optional().default(false),
    errorCount: z.int().optional().default(0),
    lastError: z.string().nullish(),
    lastUpdated: z.string().nullish(),
    completedAt: z.string().nullish(),
    productsSucceeded: z.int().optional().default(0),
    productsFailed: z.int().optional().default(0),
    progress: z.number().optional().default(0)
});

/**
 * ProductRaw
 *
 * Raw projection of a productsImport document.
 *
 * The three section dicts are 1:1 dumps of the corresponding subtrees on
 * ``FirestoreProductImport`` (``model_dump(by_alias=True, exclude_none=True)``).
 * The shape is intentionally not re-modeled here so the wire stays in sync
 * with what the import pipeline actually writes; consumers can rely on
 * Firestore field names directly.
 */
export const zProductRaw = z.object({
    id: z.string(),
    header: z.record(z.string(), z.unknown()).optional(),
    baseProduct: z.record(z.string(), z.unknown()).optional(),
    rawOnixV3: z.record(z.string(), z.unknown()).optional()
});

/**
 * ProductSource
 */
export const zProductSource = z.enum(['ONIX_IMPORT', 'MANUSCRIPT_ONLY']);

/**
 * SEARCH_TERMS_TYPE
 */
export const zSearchTermsType = z.enum(['AMAZON']);

/**
 * FirestoreSearchTerm
 *
 * Model for search terms stored in Firestore - matches existing trend model structure
 */
export const zFirestoreSearchTerm = z.object({
    id: z.string(),
    uuid: z.string(),
    term: z.string(),
    region: z.string(),
    rank: z.int(),
    searchFrequencyRank: z.int(),
    startDate: z.iso.datetime(),
    trendType: zSearchTermsType
});

/**
 * SeasonalEventDateKind
 */
export const zSeasonalEventDateKind = z.enum([
    'actual',
    'observed',
    'campaign'
]);

/**
 * EasterOffsetRule
 */
export const zEasterOffsetRule = z.object({
    type: z.literal('easter_offset'),
    date_kind: zSeasonalEventDateKind,
    days: z.int().gte(-80).lte(250)
});

/**
 * FixedDateRule
 */
export const zFixedDateRule = z.object({
    type: z.literal('fixed_date'),
    date_kind: zSeasonalEventDateKind,
    month: z.int().gte(1).lte(12),
    day: z.int().gte(1).lte(31)
});

/**
 * FixedSpanRule
 */
export const zFixedSpanRule = z.object({
    type: z.literal('fixed_span'),
    date_kind: zSeasonalEventDateKind,
    start_month: z.int().gte(1).lte(12),
    start_day: z.int().gte(1).lte(31),
    end_month: z.int().gte(1).lte(12),
    end_day: z.int().gte(1).lte(31)
});

/**
 * SeasonalEventDateSource
 */
export const zSeasonalEventDateSource = z.object({
    type: z.enum(['generated', 'curated']),
    reference: z.string().min(1),
    url: z.url().min(1).nullish(),
    checked_at: z.iso.date().nullish()
});

/**
 * SeasonalEventInternationalMatch
 *
 * The International event that a regional event replaces for its marketplace.
 */
export const zSeasonalEventInternationalMatch = z.object({
    event_id: z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    method: z.enum([
        'slug',
        'alias',
        'name'
    ])
});

/**
 * SeasonalEventOccurrence
 */
export const zSeasonalEventOccurrence = z.object({
    year: z.int().gte(1),
    date_kind: zSeasonalEventDateKind,
    start: z.iso.date(),
    end: z.iso.date(),
    source: zSeasonalEventDateSource
});

/**
 * SeasonalEventOverride
 */
export const zSeasonalEventOverride = z.object({
    year: z.int().gte(1),
    date_kind: zSeasonalEventDateKind,
    action: z.enum(['replace', 'suppress']),
    start: z.iso.date().nullish(),
    end: z.iso.date().nullish(),
    source: zSeasonalEventDateSource
});

/**
 * SeasonalEventProvenance
 */
export const zSeasonalEventProvenance = z.object({
    pipeline_version: z.string().min(1),
    template_version: z.string().min(1),
    model: z.string().min(1),
    parameters: z.record(z.string(), zJsonValue),
    inputs: z.record(z.string(), zJsonValue),
    generated_at: z.iso.datetime(),
    langfuse_trace_id: z.string().nullish()
});

/**
 * SeasonalEventPrompt
 */
export const zSeasonalEventPrompt = z.object({
    prompt_id: z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    prompt_revision: z.int().gte(1),
    text: z.string().min(1),
    provenance: zSeasonalEventProvenance.nullish()
});

/**
 * SeasonalEventRegion
 */
export const zSeasonalEventRegion = z.enum([
    'us',
    'uk',
    'de',
    'aus',
    'international'
]);

/**
 * SeasonalEventWeekday
 */
export const zSeasonalEventWeekday = z.enum([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
]);

/**
 * NthWeekdayRule
 */
export const zNthWeekdayRule = z.object({
    type: z.literal('nth_weekday'),
    date_kind: zSeasonalEventDateKind,
    month: z.int().gte(1).lte(12),
    weekday: zSeasonalEventWeekday,
    ordinal: z.union([
        z.literal(-1),
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5)
    ])
});

/**
 * SeasonalEvent
 */
export const zSeasonalEvent = z.object({
    event_id: z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    display_name: z.string().min(1),
    region: zSeasonalEventRegion,
    lead_time_months: z.int().gte(0).lte(12),
    date_rule: z.discriminatedUnion('type', [
        zFixedDateRule,
        zNthWeekdayRule,
        zEasterOffsetRule,
        zFixedSpanRule
    ]).nullable(),
    occurrences: z.array(zSeasonalEventOccurrence).optional(),
    overrides: z.array(zSeasonalEventOverride).optional(),
    prompts: z.array(zSeasonalEventPrompt).optional(),
    provenance: zSeasonalEventProvenance.nullish(),
    categories: z.array(z.string()).optional(),
    evidence: z.array(z.string()).optional(),
    judge_reasoning: z.string().nullish(),
    international_match: zSeasonalEventInternationalMatch.nullish()
});

/**
 * SemanticDataCharacter
 */
export const zSemanticDataCharacter = z.object({
    name: z.string().nullish(),
    age: z.union([
        z.string(),
        z.int()
    ]).nullish(),
    pronouns: z.string().nullish(),
    role: z.string().nullish(),
    description: z.string().nullish(),
    aliases: z.array(z.string()).nullish()
});

/**
 * SemanticDataMetadata
 */
export const zSemanticDataMetadata = z.object({
    title: z.string().nullish(),
    author: z.string().nullish(),
    language: z.string().nullish()
});

/**
 * SemanticDataWorldSetting
 */
export const zSemanticDataWorldSetting = z.object({
    settingType: z.string().nullish(),
    location: z.string().nullish(),
    timePeriod: z.string().nullish(),
    locationLabel: z.string().nullish(),
    timePeriodLabel: z.string().nullish(),
    localSocialStructure: z.string().nullish()
});

/**
 * SemanticDataWorldSettings
 */
export const zSemanticDataWorldSettings = z.object({
    settings: z.array(zSemanticDataWorldSetting).nullish(),
    socialStructure: z.string().nullish(),
    metaphysics: z.string().nullish(),
    socialStructureLabel: z.string().nullish(),
    metaphysicsLabel: z.string().nullish()
});

/**
 * FirestoreSemanticData
 */
export const zFirestoreSemanticData = z.object({
    id: z.string(),
    reportId: z.string(),
    organisation: zFirestoreOrganisationReference,
    productId: z.string(),
    workId: z.string().nullish(),
    status: z.string().optional().default('COMPLETED'),
    paragraphHistory: z.array(z.string()).optional(),
    metadata: zSemanticDataMetadata.nullish(),
    synopsis: z.string().nullish(),
    genres: z.array(z.string()).nullish(),
    worldSettings: zSemanticDataWorldSettings.nullish(),
    characters: z.array(zSemanticDataCharacter).nullish(),
    rawPayload: z.record(z.string(), z.unknown()),
    receivedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime().nullish()
});

/**
 * SeriesLevel
 */
export const zSeriesLevel = z.enum(['collection', 'subcollection']);

/**
 * SeriesNumberSource
 */
export const zSeriesNumberSource = z.enum(['collection_sequence', 'part_number']);

/**
 * SeriesOrderType
 *
 * ONIX List 197 (collection sequence type).
 */
export const zSeriesOrderType = z.enum([
    'proprietary',
    'title_order',
    'publication_order',
    'temporal_narrative_order',
    'original_publication_order',
    'suggested_reading_order',
    'suggested_display_order'
]);

/**
 * FirestoreProductSeriesItem
 *
 * One ONIX <Collection> the product belongs to, as the publisher sent it.
 */
export const zFirestoreProductSeriesItem = z.object({
    title: z.string().nullish(),
    level: zSeriesLevel.nullish(),
    number: z.string().nullish(),
    numberSource: zSeriesNumberSource.nullish(),
    orderType: zSeriesOrderType.nullish(),
    identifiers: z.array(zFirestoreProductSeriesIdentifier).optional().default([])
});

/**
 * SeriesStatus
 */
export const zSeriesStatus = z.enum([
    'in_series',
    'not_in_series',
    'unknown'
]);

/**
 * FirestoreProductSeries
 */
export const zFirestoreProductSeries = z.object({
    status: zSeriesStatus.optional().default('unknown'),
    items: z.array(zFirestoreProductSeriesItem).optional().default([])
});

/**
 * SharedData
 */
export const zSharedData = z.object({
    defaultTargetGroups: z.array(z.string()).optional(),
    ProductAvailability: z.record(z.string(), z.string()).optional(),
    themaMajorSubjectCodes: z.array(z.string()).optional(),
    themaMajorQualifierCodes: z.array(z.string()).optional()
});

/**
 * SubjectSource
 */
export const zSubjectSource = z.enum([
    'GENERATED',
    'DATABASE',
    'MANUAL'
]);

/**
 * FirestoreProductBisacSubject
 *
 * A BISAC code on a product, ordered by list position until the user selects it.
 *
 * A ``MANUAL`` entry is always ``final``: it exists only because the user selected
 * it, and an entry that is neither ``inSource`` nor ``final`` is deleted on the next
 * import.
 */
export const zFirestoreProductBisacSubject = z.object({
    id: z.string(),
    subjectCode: z.string().optional().default(''),
    subjectHeadingText: z.string().nullish(),
    source: zSubjectSource.optional().default('DATABASE'),
    inSource: z.boolean().optional().default(false),
    final: z.boolean().optional().default(false),
    orderIndex: z.int().nullish(),
    seq: z.int().nullish()
});

/**
 * FirestoreProductSubject
 */
export const zFirestoreProductSubject = z.object({
    id: z.string().nullish(),
    responseId: z.string().nullish(),
    source: zSubjectSource.nullish().default('MANUAL'),
    subjectCode: z.string().nullish(),
    subjectHeadingText: z.string().nullish(),
    reasoning: z.string().nullish(),
    subjectSchemeIdentifier: z.string().nullish(),
    ranking: z.int().nullish(),
    rankingReasoning: z.string().nullish(),
    keywordSearchQuery: z.string().nullish(),
    databaseIndex: z.int().nullish(),
    hotnessScore: z.number().nullish()
});

/**
 * FirestoreProductSubjects
 */
export const zFirestoreProductSubjects = z.object({
    generated: z.array(zFirestoreProductSubject).nullish(),
    database: z.array(zFirestoreProductSubject).nullish(),
    final: z.array(zFirestoreProductSubject).nullish(),
    finalWords: z.array(zFirestoreProductSubject).nullish(),
    bisac: z.array(zFirestoreProductBisacSubject).optional()
});

/**
 * FirestoreProduct
 */
export const zFirestoreProduct = z.object({
    id: z.string(),
    identifier: z.string(),
    identifierType: z.string().nullish(),
    identifiers: z.array(zFirestoreProductIdentifier).optional().default([]),
    workId: z.string().nullish(),
    importData: zFirestoreProductImportData.nullish(),
    title: z.string(),
    descriptions: z.array(zFirestoreProductDescription).nullish().default([]),
    subtitles: z.array(zFirestoreProductSubtitleCandidate).optional().default([]),
    biography: z.string().nullish(),
    author: z.array(z.string()).optional().default([]),
    genre: z.array(z.string()).optional().default([]),
    keywordCount: z.int().optional().default(0),
    language: z.string(),
    languageOriginal: z.string(),
    organisation: zFirestoreOrganisationReference,
    productGroup: zProductFormCodeLiteral,
    productGroupDescription: zProductFormDescLiteral,
    publisher: z.string(),
    publishers: z.array(zFirestoreProductPublisher).optional().default([]),
    imprints: z.array(z.string()).optional().default([]),
    publishedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    subjects: zFirestoreProductSubjects.nullish(),
    forceSyncTriggeredAt: z.iso.datetime().nullish(),
    categorization: zFirestoreProductCategorization.nullish().default({
        fiction: true,
        nonFiction: true,
        children: true,
        updatedAt: null,
        inferredFrom: null
    }),
    sourceType: zProductSource.optional().default('ONIX_IMPORT'),
    manuscript: zFirestoreProductManuscript.nullish(),
    actions: zFirestoreProductActions.optional(),
    isEligibleSearch: z.boolean().optional().default(true),
    isEligibleCheckout: z.boolean().optional().default(false),
    supplyDetails: z.array(zFirestoreProductSupplyDetail).optional(),
    dimensions: zFirestoreProductDimensions.nullish(),
    ageRanges: zFirestoreProductAgeRanges.nullish(),
    images: zFirestoreProductImages.optional(),
    quotes: z.array(zFirestoreProductQuote).optional().default([]),
    series: zFirestoreProductSeries.optional()
});

/**
 * TRENDS_TYPE
 */
export const zTrendsType = z.enum(['GOOGLE', 'AMAZON']);

/**
 * FirestoreTrend
 */
export const zFirestoreTrend = z.object({
    id: z.string().nullish(),
    uuid: z.string(),
    term: z.string(),
    region: z.string(),
    rank: z.int(),
    startDate: z.iso.datetime(),
    trendType: zTrendsType
});

/**
 * FirestoreProductsByTrend
 */
export const zFirestoreProductsByTrend = z.object({
    id: z.string(),
    organisation: zFirestoreOrganisationReference,
    newProducts: z.array(z.string()).optional().default([]),
    trend: zFirestoreTrend
});

/**
 * UserRole
 */
export const zUserRole = z.enum([
    'SUPER_ADMIN',
    'ADMIN',
    'MEMBER'
]);

/**
 * FirestoreUser
 */
export const zFirestoreUser = z.object({
    id: z.string(),
    userId: z.string(),
    email: z.string(),
    emailVerified: z.boolean().optional().default(false),
    givenName: z.string().nullish(),
    familyName: z.string().nullish(),
    organisation: zFirestoreOrganisationReference.nullish(),
    apiKeys: z.array(zFirestoreUserApiKey).nullish().default([]),
    roles: z.array(zUserRole).nullish().default([]),
    deletedAt: z.iso.datetime().nullish()
});
